/** React over the game store: a context for the game, selectors with useSyncExternalStore, the locale. */
import { createContext, useContext, useEffect, useReducer, useSyncExternalStore } from 'react';

import type { Game } from '@game/game';
import type { GameState } from '@game/types';
import { getLocale, onLocaleChange, type Locale } from '@i18n/index';

export const GameContext = createContext<Game | null>(null);

export function useGame(): Game {
  const game = useContext(GameContext);
  if (!game) throw new Error('useGame outside GameContext');
  return game;
}

/** A value from the store. The selector must return the same value for the same state (a primitive or a stored reference). */
export function useGameState<T>(selector: (state: GameState) => T): T {
  const game = useGame();
  return useSyncExternalStore(
    (listener) => game.store.subscribe(listener),
    () => selector(game.store.get()),
    () => selector(game.store.get()),
  );
}

/** Re-renders every `ms` while it is not null: for what changes with time, not with the store. */
export function useTick(ms: number | null): void {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (ms === null) return;
    const timer = setInterval(bump, ms);
    return () => clearInterval(timer);
  }, [ms]);
}

/** Re-renders when the locale changes. */
export function useLocale(): Locale {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => onLocaleChange(bump), []);
  return getLocale();
}
